import { useState, useRef } from "react";
import {
  LoadingOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Progress, Spin } from "antd";
import useUploadMedia from "hooks/useUploadMedia/useUploadMedia";
import { renderErrorNotifications } from "helpers/error.helpers";
import "./MediaUpload.css";

const IMAGE_EXTENSIONS = ".jpg,.jpeg,.png,.gif,.webp";

/** Resolves image dimensions from a File object. */
const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject();
    };
    img.src = url;
  });

const optimizeImage = (file, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const maxWidth = img.width * 0.8;
        const maxHeight = img.height * 0.8;
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            width = maxWidth;
            height = (img.height * maxWidth) / img.width;
          } else {
            height = maxHeight;
            width = (img.width * maxHeight) / img.height;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(
                new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                })
              );
            } else {
              reject(new Error("Failed to optimize image"));
            }
          },
          file.type,
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Form.Item-compatible image upload field.
 *
 * Renders its own preview (no Ant Upload list) to avoid CSSMotion wobble.
 * Uploads immediately on file select via useUploadMedia and propagates
 * the remote URL back to the parent Form field via onChange.
 *
 * Props:
 * - value / onChange  — URL string, Form.Item compatible
 * - listType          — "picture-circle" | "picture-card" (default)
 * - size              — "default" | "small"
 * - optimize          — compress image before upload (default false)
 * - maxSizeMB         — max file size in MB (default 5)
 * - allowUrlInput     — show manual URL text input (default false)
 * - aspectRatio       — { width: number, height: number, enforce: boolean }
 *                       e.g. { width: 16, height: 9, enforce: true }
 *                       When enforce=true, rejects images outside the ratio (±5% tolerance).
 *                       When enforce=false, ratio is only shown in the label — no validation.
 * - minDimensions      — { width: number, height: number }
 *                       e.g. { width: 400, height: 400 }
 *                       Rejects images whose width or height is below the specified minimums.
 */
function MediaUpload({
  value,
  onChange,
  disabled,
  listType = "picture-card",
  size = "default",
  maxSizeMB = 5,
  accept = IMAGE_EXTENSIONS,
  optimize = true,
  allowUrlInput = false,
  className = "",
  aspectRatio = null,
  minDimensions = null,
}) {
  const inputRef = useRef(null);
  const { upload, uploading, percent } = useUploadMedia();
  const [urlValue, setUrlValue] = useState(value || "");
  // Local blob preview — shown immediately on pick so there's zero
  // visual gap between selecting a file and seeing the thumbnail.
  const [localPreview, setLocalPreview] = useState(null);

  const isCircle = listType === "picture-circle";
  const isSmall = size === "small";
  const isPortrait =
    aspectRatio && aspectRatio.height > aspectRatio.width && !isCircle;
  const displayUrl = localPreview || value || "";
  const hasImage = Boolean(displayUrl);

  // Once the remote URL lands, silently drop the local blob.
  if (localPreview && value && value !== localPreview) {
    URL.revokeObjectURL(localPreview);
    setLocalPreview(null);
  }

  const slotCls = [
    "media-upload",
    isCircle ? "media-upload--circle" : "media-upload--card",
    isSmall ? "media-upload--sm" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleTriggerClick = () => {
    if (disabled || uploading || hasImage) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > maxSizeMB * 1024 * 1024) {
      renderErrorNotifications([
        { message: `File size must be less than ${maxSizeMB}MB` },
      ]);
      return;
    }
    if (!file.type.startsWith("image/")) {
      renderErrorNotifications([{ message: "Please upload an image file" }]);
      return;
    }

    if (aspectRatio?.enforce) {
      try {
        const { width, height } = await getImageDimensions(file);
        const expected = aspectRatio.width / aspectRatio.height;
        const actual = width / height;
        const tolerance = 0.05; // 5% leeway
        if (Math.abs(actual - expected) > expected * tolerance) {
          renderErrorNotifications([
            {
              message: `Image must be ${aspectRatio.width}:${aspectRatio.height} ratio (e.g. 1920×${Math.round((1920 * aspectRatio.height) / aspectRatio.width)}px). Uploaded image is ${width}×${height}px.`,
            },
          ]);
          return;
        }
      } catch {
        // If we can't read dimensions, allow the upload
      }
    }

    if (minDimensions) {
      try {
        const { width, height } = await getImageDimensions(file);
        if (width < minDimensions.width || height < minDimensions.height) {
          renderErrorNotifications([
            {
              message: `Image must be at least ${minDimensions.width}×${minDimensions.height}px. Uploaded image is ${width}×${height}px.`,
            },
          ]);
          return;
        }
      } catch {
        // If we can't read dimensions, allow the upload
      }
    }

    setLocalPreview(URL.createObjectURL(file));

    let fileToUpload = file;
    if (optimize) {
      try {
        fileToUpload = await optimizeImage(file);
      } catch {
        // fall back to original on optimization failure
      }
    }

    const url = await upload(fileToUpload);
    if (url) {
      onChange?.(url);
    } else {
      // Upload failed — clear the local preview so the UI resets
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(null);
    onChange?.(null);
  };

  const handlePreview = (e) => {
    e.stopPropagation();
    if (displayUrl) window.open(displayUrl, "_blank", "noopener,noreferrer");
  };

  const handleUrlChange = (ev) => {
    const url = ev.target.value;
    setUrlValue(url);
    onChange?.(url);
  };

  const portraitStyle = isPortrait
    ? {
        height: "auto",
        aspectRatio: `${aspectRatio.width} / ${aspectRatio.height}`,
        maxHeight: 320,
      }
    : undefined;

  return (
    <div className={slotCls} style={portraitStyle}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="media-upload__input"
        onChange={handleFileChange}
      />

      {allowUrlInput && (
        <input
          type="text"
          className="media-upload__url-input"
          placeholder="Enter image URL"
          value={urlValue}
          onChange={handleUrlChange}
        />
      )}

      {hasImage ? (
        <div className="media-upload__preview">
          <img
            src={displayUrl}
            alt=""
            className="media-upload__img"
            style={
              isPortrait
                ? {
                    objectFit: "contain",
                    backgroundColor: "var(--color-bg-layout, #f5f5f5)",
                  }
                : undefined
            }
          />

          {uploading && (
            <div className="media-upload__progress-overlay">
              <Progress
                type="circle"
                percent={percent}
                size={isCircle ? 48 : 56}
                strokeColor="white"
                trailColor="rgba(255,255,255,0.3)"
                format={(p) => (
                  <span style={{ color: "white", fontSize: 12 }}>{p}%</span>
                )}
              />
            </div>
          )}

          {!uploading && (
            <div className="media-upload__actions">
              <button
                type="button"
                className="media-upload__btn"
                onClick={handlePreview}
                aria-label="Preview"
              >
                <EyeOutlined />
              </button>
              <button
                type="button"
                className="media-upload__btn media-upload__btn--danger"
                onClick={handleRemove}
                disabled={disabled}
                aria-label="Remove"
              >
                <DeleteOutlined />
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="media-upload__trigger"
          onClick={handleTriggerClick}
          disabled={disabled || uploading}
          aria-label="Upload image"
        >
          {uploading ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} />} />
          ) : (
            <>
              <span className="media-upload__plus">+</span>
              <span className="media-upload__label">Upload</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default MediaUpload;
